import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import { API_URL, USER_POOL_ID, USER_POOL_CLIENT_ID } from '../config';
import { formatDateTime } from '../utils/dateFormat';
import './OrganizationManager.css';

const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: USER_POOL_CLIENT_ID,
});

const ACCEPT_LINK = (id) => `https://dms.16bit.ai/accept-invitation?token=${id}`;

// Decode the ID token's claims (already verified by Cognito; used for display
// and to pick the admin layout; the API enforces authorization on its own).
function claimsOf(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    const raw = payload['cognito:groups'];
    const groups = Array.isArray(raw) ? raw : String(raw || '').replace(/^\[|\]$/g, '').split(',').map((s) => s.trim()).filter(Boolean);
    return { email: payload.email || '', isAdmin: groups.includes('Admin') || (payload.email || '').endsWith('@16bit.ai') };
  } catch (e) {
    return { email: '', isAdmin: false };
  }
}

function getSessionToken() {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) return reject(new Error('Not signed in'));
    cognitoUser.getSession((err, session) => {
      if (err || !session || !session.isValid()) return reject(err || new Error('Session expired'));
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

const initialOf = (email) => (email || '?').trim().charAt(0);

function expiresText(v) {
  if (!v) return '';
  const ms = typeof v === 'number' ? v * 1000 : Date.parse(v);
  return Number.isFinite(ms) ? formatDateTime(ms) : '';
}

export default function OrganizationManager() {
  const navigate = useNavigate();
  const tokenRef = useRef(null);
  const [me, setMe] = useState({ email: '', isAdmin: false });
  const [orgs, setOrgs] = useState([]);
  // id -> { members, invitations, ghcrKey, loading }
  const [details, setDetails] = useState({});
  const [inviteTarget, setInviteTarget] = useState(null); // org id the invite input belongs to
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null); // { kind: 'ok'|'error', text }
  const [busy, setBusy] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // org id
  const [inviteEmail, setInviteEmail] = useState('');
  const [lastInvite, setLastInvite] = useState(null);
  const [keyDraft, setKeyDraft] = useState(null); // string while editing, null otherwise

  const say = (kind, text) => setBanner({ kind, text });

  // All API calls go through here so the token is always the one from the
  // session, never a state value that might not be set yet (that was the
  // "select an organization before anything loads" bug).
  const api = useCallback(async (path, options = {}) => {
    const token = tokenRef.current || (tokenRef.current = await getSessionToken());
    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(options.headers || {}) },
    });
    let body = null;
    try { body = await res.json(); } catch (e) { body = null; }
    if (!res.ok) {
      const msg = (body && (body.message || body.error || body.detail)) || `Request failed (${res.status})`;
      throw new Error(msg);
    }
    return body;
  }, []);

  const loadDetails = useCallback(async (orgId) => {
    setDetails((d) => ({ ...d, [orgId]: { ...(d[orgId] || {}), loading: true } }));
    const [members, invitations, key] = await Promise.all([
      api(`/organizations/${orgId}/members`).then((r) => r.members || []).catch(() => []),
      api(`/invitations?organization_id=${orgId}`).then((r) => (Array.isArray(r) ? r : [])).catch(() => []),
      api(`/organizations/${orgId}/ghcr-key`).then((r) => r.ghcr_key || '').catch(() => ''),
    ]);
    setDetails((d) => ({ ...d, [orgId]: { members, invitations, ghcrKey: key, loading: false } }));
  }, [api]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/organizations');
      const list = Array.isArray(data) ? data : (data && data.organizations) || [];
      const normalized = list
        .map((o) => ({ id: o.id || o.organization_id, name: o.name, type: o.type || 'distributor' }))
        .sort((a, b) => (a.type === 'admin') - (b.type === 'admin') || a.name.localeCompare(b.name));
      setOrgs(normalized);
      // Members for every organization in parallel: the table shows them inline.
      await Promise.all(normalized.map((o) => loadDetails(o.id)));
    } catch (e) {
      say('error', `Could not load organizations: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [api, loadDetails]);

  useEffect(() => {
    (async () => {
      try {
        const token = await getSessionToken();
        tokenRef.current = token;
        setMe(claimsOf(token));
        await loadAll();
      } catch (e) {
        setLoading(false);
        say('error', 'Your session has expired. Sign in again to manage organizations.');
      }
    })();
  }, [loadAll]);

  // ---- actions ------------------------------------------------------------

  const createOrganization = async () => {
    const name = newOrgName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const created = await api('/organizations', { method: 'POST', body: JSON.stringify({ name, type: 'distributor', creator_email: me.email }) });
      setNewOrgName('');
      setShowCreate(false);
      say('ok', `Created ${created.name}. You were added as its first member.`);
      await loadAll();
    } catch (e) {
      say('error', `Could not create organization: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const deleteOrganization = async (org) => {
    setBusy(true);
    try {
      const r = await api(`/organizations/${org.id}`, { method: 'DELETE' });
      const n = (r && r.memberships_removed) || 0;
      say('ok', `Deleted ${org.name}${n ? ` and removed ${n} member${n === 1 ? '' : 's'}` : ''}.`);
      setConfirmDelete(null);
      await loadAll();
    } catch (e) {
      say('error', `Could not delete ${org.name}: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const sendInvitation = async (org) => {
    const email = inviteEmail.trim();
    if (!email) return;
    setBusy(true);
    try {
      const inv = await api('/invitations', { method: 'POST', body: JSON.stringify({ email, organization_id: org.id, organization_name: org.name }) });
      setInviteEmail('');
      setLastInvite(inv);
      say('ok', `Invited ${email} to ${org.name}. The invitation email has been sent; the link below also works.`);
      await loadDetails(org.id);
    } catch (e) {
      say('error', `Could not invite ${email}: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const cancelInvitation = async (org, inv) => {
    setBusy(true);
    try {
      await api(`/invitations/cancel/${inv.id}`, { method: 'DELETE' });
      say('ok', `Cancelled the invitation for ${inv.email}.`);
      await loadDetails(org.id);
    } catch (e) {
      say('error', `Could not cancel the invitation: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (org, email) => {
    if (!window.confirm(`Remove ${email} from ${org.name}? They will lose access to its installations.`)) return;
    setBusy(true);
    try {
      await api(`/organizations/${org.id}/members/${encodeURIComponent(email)}`, { method: 'DELETE' });
      say('ok', `Removed ${email} from ${org.name}.`);
      await loadDetails(org.id);
    } catch (e) {
      say('error', `Could not remove ${email}: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const saveKey = async (org) => {
    setBusy(true);
    try {
      const r = await api(`/organizations/${org.id}/ghcr-key`, { method: 'PUT', body: JSON.stringify({ ghcr_key: keyDraft }) });
      setDetails((d) => ({ ...d, [org.id]: { ...d[org.id], ghcrKey: r.ghcr_key || '' } }));
      setKeyDraft(null);
      say('ok', `Saved the registry key for ${org.name}.`);
    } catch (e) {
      say('error', `Could not save the registry key: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      say('ok', `${label} copied to clipboard.`);
    } catch (e) {
      say('error', 'Copy failed. Select the text and copy it manually.');
    }
  };

  // ---- render -------------------------------------------------------------

  const totals = useMemo(() => ({
    members: orgs.reduce((n, o) => n + ((details[o.id] || {}).members || []).length, 0),
    pending: orgs.reduce((n, o) => n + ((details[o.id] || {}).invitations || []).filter((i) => i.status === 'pending').length, 0),
  }), [orgs, details]);

  const [keyOpen, setKeyOpen] = useState(null); // org id whose registry key is shown

  const renderOrganization = (org) => {
    const d = details[org.id] || {};
    const members = d.members || [];
    const pending = (d.invitations || []).filter((i) => i.status === 'pending');
    const isAdminOrg = org.type === 'admin';
    const showKey = keyOpen === org.id;
    const editingKey = keyDraft !== null && showKey;

    return (
      <section className="org-card" key={org.id} aria-label={org.name}>
        <header className="org-card__header">
          <div className="org-card__identity">
            <span className="org-card__avatar" aria-hidden="true">{initialOf(org.name)}</span>
            <div>
              <h2 className="org-card__name">{org.name}</h2>
              <div className="org-card__meta">
                <span className={`orgs-type${isAdminOrg ? ' orgs-type--admin' : ''}`}>{isAdminOrg ? '16 Bit' : 'Distributor'}</span>
                <span>{members.length} member{members.length === 1 ? '' : 's'}</span>
                {pending.length > 0 && <span>{pending.length} invited</span>}
              </div>
            </div>
          </div>
          <div className="org-card__actions">
            <button type="button" className={`orgs-btn orgs-btn--small${showKey ? ' orgs-btn--active' : ''}`} onClick={() => { setKeyOpen(showKey ? null : org.id); setKeyDraft(null); }}>
              Registry key{d.ghcrKey ? '' : ' (not set)'}
            </button>
            {me.isAdmin && !isAdminOrg && confirmDelete !== org.id && (
              <button type="button" className="orgs-btn orgs-btn--small orgs-btn--danger" disabled={busy} onClick={() => setConfirmDelete(org.id)}>
                Delete
              </button>
            )}
          </div>
        </header>

        {confirmDelete === org.id && (
          <div className="orgs-confirm">
            <span>
              Delete <strong>{org.name}</strong>? Its {members.length} member{members.length === 1 ? '' : 's'} lose access and pending invitations are cancelled. Installations are not affected.
            </span>
            <span className="orgs-confirm__actions">
              <button type="button" className="orgs-btn orgs-btn--small" disabled={busy} onClick={() => setConfirmDelete(null)}>Keep it</button>
              <button type="button" className="orgs-btn orgs-btn--small orgs-btn--danger" disabled={busy} onClick={() => deleteOrganization(org)}>Delete organization</button>
            </span>
          </div>
        )}

        {showKey && (
          <div className="org-card__key">
            <div className="org-card__key-text">
              <strong>Container registry key</strong>
              <span className="orgs-muted"> used by this organization's installations to pull Rho images from GHCR.</span>
            </div>
            {editingKey ? (
              <>
                <textarea className="orgs-key-input" value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)} placeholder="Paste the GHCR token" autoFocus />
                <div className="orgs-form-row">
                  <button type="button" className="orgs-btn orgs-btn--primary orgs-btn--small" disabled={busy} onClick={() => saveKey(org)}>Save key</button>
                  <button type="button" className="orgs-btn orgs-btn--small" disabled={busy} onClick={() => setKeyDraft(null)}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="orgs-key">{d.ghcrKey ? d.ghcrKey : <span className="orgs-muted">No key set</span>}</div>
                <div className="orgs-form-row">
                  {d.ghcrKey && <button type="button" className="orgs-btn orgs-btn--small" onClick={() => copy(d.ghcrKey, 'Registry key')}>Copy key</button>}
                  {me.isAdmin && (
                    <button type="button" className="orgs-btn orgs-btn--small" onClick={() => setKeyDraft(d.ghcrKey || '')}>{d.ghcrKey ? 'Replace key' : 'Add key'}</button>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <table className="orgs-table">
          <thead>
            <tr>
              <th>Member</th>
              <th style={{ width: 160 }}>Status</th>
              <th style={{ width: 200 }}>Since</th>
              {me.isAdmin && <th style={{ width: 110 }} aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {d.loading && members.length === 0 && pending.length === 0 && (
              <tr><td colSpan={me.isAdmin ? 4 : 3} className="orgs-muted">Loading…</td></tr>
            )}
            {!d.loading && members.length === 0 && pending.length === 0 && (
              <tr><td colSpan={me.isAdmin ? 4 : 3} className="orgs-muted">No members yet{me.isAdmin ? '. Invite someone below.' : '.'}</td></tr>
            )}
            {members.map((m) => (
              <tr key={m.id || m.user_email}>
                <td>
                  <span className="orgs-person">
                    <span className="orgs-chip__avatar">{initialOf(m.user_email)}</span>
                    <span>{m.user_email}{m.user_email === me.email ? <span className="orgs-muted"> (you)</span> : null}</span>
                  </span>
                </td>
                <td><span className="orgs-status orgs-status--active">Active</span></td>
                <td className="orgs-muted">{m.added_at ? formatDateTime((Number(m.added_at) || 0) * 1000) : '—'}</td>
                {me.isAdmin && (
                  <td className="orgs-table__actions">
                    <button type="button" className="orgs-btn orgs-btn--small orgs-btn--danger" disabled={busy} onClick={() => removeMember(org, m.user_email)}>Remove</button>
                  </td>
                )}
              </tr>
            ))}
            {pending.map((inv) => (
              <tr key={inv.id} className="orgs-row--pending">
                <td>
                  <span className="orgs-person">
                    <span className="orgs-chip__avatar orgs-chip__avatar--pending">{initialOf(inv.email)}</span>
                    <span>{inv.email}</span>
                  </span>
                </td>
                <td><span className="orgs-status orgs-status--pending">Invited</span></td>
                <td className="orgs-muted">expires {expiresText(inv.expires_at) || 'soon'}</td>
                {me.isAdmin && (
                  <td className="orgs-table__actions">
                    <button type="button" className="orgs-btn orgs-btn--small" disabled={busy} onClick={() => cancelInvitation(org, inv)}>Cancel</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {me.isAdmin && (
            <tfoot>
              <tr>
                <td colSpan={4}>
                  <div className="orgs-form-row">
                    <input
                      type="email"
                      placeholder="Invite someone: name@company.com"
                      value={inviteTarget === org.id ? inviteEmail : ''}
                      onFocus={() => { if (inviteTarget !== org.id) { setInviteTarget(org.id); setInviteEmail(''); } }}
                      onChange={(e) => { setInviteTarget(org.id); setInviteEmail(e.target.value); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') sendInvitation(org); }}
                    />
                    <button type="button" className="orgs-btn orgs-btn--primary orgs-btn--small" disabled={busy || inviteTarget !== org.id || !inviteEmail.trim()} onClick={() => sendInvitation(org)}>
                      Send invitation
                    </button>
                    {lastInvite && lastInvite.organization_id === org.id && (
                      <span className="orgs-invite-link">
                        <input readOnly value={ACCEPT_LINK(lastInvite.id)} onFocus={(e) => e.target.select()} aria-label="Invitation link" />
                        <button type="button" className="orgs-btn orgs-btn--small" onClick={() => copy(ACCEPT_LINK(lastInvite.id), 'Invitation link')}>Copy link</button>
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </section>
    );
  };

  return (
    <div className="orgs-page">
      <div className="orgs-header">
        <div className="orgs-header__brand">
          <img src="/16bit-primary-logo-reverse.svg" alt="16 Bit" />
          <div className="orgs-header__title">
            <div className="orgs-header__eyebrow">Distributor Management System</div>
            <h1>Organizations</h1>
          </div>
        </div>
        <div className="orgs-header__actions">
          <button type="button" className="orgs-btn" onClick={() => navigate('/')}>← Installations</button>
          {me.isAdmin && (
            <button type="button" className="orgs-btn orgs-btn--primary" onClick={() => { setShowCreate((v) => !v); setNewOrgName(''); }}>
              New organization
            </button>
          )}
        </div>
      </div>

      {banner && (
        <div className={`orgs-banner orgs-banner--${banner.kind}`} role="status">
          <span>{banner.text}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setBanner(null)}>×</button>
        </div>
      )}

      {!loading && orgs.length > 0 && (
        <p className="orgs-note">
          {me.isAdmin
            ? `${orgs.length} organization${orgs.length === 1 ? '' : 's'}, ${totals.members} member${totals.members === 1 ? '' : 's'}${totals.pending ? `, ${totals.pending} pending invitation${totals.pending === 1 ? '' : 's'}` : ''}.`
            : 'The people in your organization. Only 16 Bit administrators can invite or remove members.'}
        </p>
      )}

      {showCreate && me.isAdmin && (
        <div className="orgs-create">
          <label htmlFor="new-org-name">Name</label>
          <input
            id="new-org-name"
            autoFocus
            placeholder="e.g. Northwind Imaging"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createOrganization(); if (e.key === 'Escape') setShowCreate(false); }}
          />
          <button type="button" className="orgs-btn orgs-btn--primary" disabled={busy || !newOrgName.trim()} onClick={createOrganization}>
            {busy ? 'Creating…' : 'Create organization'}
          </button>
          <button type="button" className="orgs-btn" disabled={busy} onClick={() => setShowCreate(false)}>Cancel</button>
          <span className="orgs-muted" style={{ fontSize: 12 }}>A distributor organization. You become its first member.</span>
        </div>
      )}

      {loading ? (
        <div className="orgs-empty">Loading organizations…</div>
      ) : orgs.length === 0 ? (
        <div className="orgs-empty">
          <strong>No organizations yet</strong>
          {me.isAdmin ? 'Create the first distributor organization with the button above.' : 'You are not a member of any organization yet. Ask your administrator for an invitation.'}
        </div>
      ) : (
        orgs.map(renderOrganization)
      )}
    </div>
  );
}
